import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Permission } from '@/entities/permissions';
import { usePeriodAbsFormatter, usePermission, useUserConfig } from '@/features/_shared/user';
import { chartOptions } from '@/features/charts/api/charts.options';
import type {
  TimeSeriesPoint,
  TimeSeriesPointCustom
} from '@/features/charts/lib/buildChartLineSeries';
import { findSeriesBasedOnKey } from '@/features/charts/lib/findSeriesBasedOnKey';
import { getShortTitle } from '@/features/charts/lib/getShortTitle';
import { parseDataToChartSeries } from '@/features/charts/lib/parseDataToChartSeries';
import type { SeriesRecord } from '@/features/charts/types/chartData';
import type { TemplateChartSeries } from '@/shared/types/charts';
import { generateMockSeries } from '@/test-utils/fixtures/chartSeries.mock';

const useChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  isDhChart = false,
  isDhChartFullscreen = false,
  isInViewport = true
) => {
  const { data: userConfigData } = useUserConfig();
  const timezone = userConfigData?.timezone.value ?? 'UTC';
  const { formatPeriodAbs } = usePeriodAbsFormatter();
  const {
    data: chartData,
    isError: isChartDataError,
    isLoading: isChartDataLoading
  } = useQuery({
    ...chartOptions.data('spot', templateChartSeries, timezone),
    enabled: templateChartSeries !== undefined && isInViewport
  });

  const takeSpotData = useCallback(
    (tsData: SeriesRecord, decimalPlaces: number): TimeSeriesPoint => {
      const x = tsData[0];
      const y = Number(tsData[1]);
      const custom: TimeSeriesPointCustom = {
        periodAbs: tsData.length > 2 ? formatPeriodAbs(tsData[2]) : '',
        decimalPlaces
      };
      return { x, y, custom };
    },
    [formatPeriodAbs]
  );
  const { hasPermission } = usePermission();

  const chartSeries = useMemo(() => {
    if (isDhChart && !hasPermission(Permission.ExportImage)) {
      return [generateMockSeries()];
    }
    if (chartData) {
      const dataSymbols = chartData.symbols;
      return dataSymbols.flatMap(({ key, metadata, series, transactionDate }, symbolIndex) => {
        const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
        const shortTitle = getShortTitle(templateSeries);
        const parseableElements = {
          color: templateSeries?.settings.color,
          symbolIndex,
          shortTitle,
          key,
          metadata,
          transactionDate
        };
        return parseDataToChartSeries(series, parseableElements, isDhChartFullscreen, takeSpotData);
      });
    }
  }, [isDhChart, hasPermission, chartData, templateChartSeries, isDhChartFullscreen, takeSpotData]);

  return {
    loading: isChartDataLoading || !templateChartSeries || (!isInViewport && !chartData),
    error: isChartDataError,
    chartSeries
  };
};

export default useChartSeries;
